/**
 * Pure expansion of a `SpatialAttackPatternV1` into the concrete attack units
 * one cast of that stage produces.
 *
 * "Pure" is load-bearing and tested: this module reads no clock, consumes no
 * global RNG, and holds no state. The same pattern and the same cast seed
 * always produce the same ordered bytes, which is what makes replay and
 * offline/authoritative parity possible at all.
 *
 * It owns **relative** geometry and **relative** release offsets only. Where
 * the cast actually is in the world, and which absolute tick offset zero
 * corresponds to, belong to the runtime scheduler (plan P3). Transforms are
 * therefore expressed in a stage-local frame: `+y` is the forward axis,
 * `directionRad` is signed clockwise-positive off that axis, and the origin is
 * the stage's own anchor.
 */
import { FORMATION_FIELD_RULES } from './field-rules.js';
import {
  formationIsWorldFixed,
  type ExpandedTransform,
  type HitGroupingV1,
  type MemberScheduleV1,
  type SpatialAttackPatternV1,
  type SpatialFormationV1,
  type VolleyScheduleV1,
} from './composite-types.js';

/**
 * One resolvable attack: a thing with its own identity and its own hit record.
 *
 * `memberIndex === null` marks a `SHARED_UNION` unit — several footprints, one
 * attack, one deduplicated hit map. That is exactly the "three-way thrust hits
 * once" case, and it is why a unit carries a *list* of transforms rather than
 * one: the footprint count and the attack count are different numbers.
 */
export interface ExpandedAttackUnit {
  readonly volleyIndex: number;
  /** `null` for SHARED_UNION; otherwise the member this unit resolves. */
  readonly memberIndex: number | null;
  /** Every formation member this unit covers. Length 1 unless SHARED_UNION. */
  readonly memberIndices: readonly number[];
  /** Ticks after this stage's own release, never after the cast's. */
  readonly releaseOffsetTicks: number;
  readonly transforms: readonly ExpandedTransform[];
}

export type SpatialExpansionResult =
  | { readonly ok: true; readonly units: readonly ExpandedAttackUnit[] }
  | { readonly ok: false; readonly code: string; readonly message: string };

function fail(code: string, message: string): SpatialExpansionResult {
  return { ok: false, code, message };
}

function isPositiveInt(value: number): boolean {
  return Number.isInteger(value) && value > 0;
}

const DEG_TO_RAD = Math.PI / 180;

/**
 * A small deterministic PRNG (mulberry32) fed only by the cast-local seed.
 *
 * Deliberately its own substream rather than a draw from any shared generator:
 * expanding a pattern must not shift what the rest of the cast rolls, so
 * adding a scatter to a skill cannot change an unrelated crit sequence.
 */
function makeSubstream(seed: number): () => number {
  let state = (seed | 0) >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Derive an independent substream per purpose so adding one randomised axis
 * later cannot silently shift another's sequence. */
function derive(seed: number, salt: number): () => number {
  return makeSubstream((Math.imul(seed | 0, 0x9e3779b1) ^ Math.imul(salt, 0x85ebca6b)) | 0);
}

/** How many attempts a rejection sampler gets before it reports the requested
 * geometry infeasible. Bounded so the expander always terminates; exceeding it
 * is reported, never worked around by placing fewer members. */
const SCATTER_PLACEMENT_ATTEMPTS_PER_MEMBER = 400;

type FormationResult =
  | { readonly ok: true; readonly transforms: readonly ExpandedTransform[] }
  | { readonly ok: false; readonly code: string; readonly message: string };

function expandFormation(formation: SpatialFormationV1, seed: number): FormationResult {
  switch (formation.kind) {
    case 'SINGLE': {
      if (formation.count !== 1) {
        return { ok: false, code: 'COMPOSITE_FORMATION_COUNT_INVALID', message: 'SINGLE formation must declare count 1' };
      }
      return { ok: true, transforms: [{ x: 0, y: 0, directionRad: 0 }] };
    }

    case 'FAN': {
      if (!FORMATION_FIELD_RULES.memberCount.holds(formation.count)) {
        return { ok: false, code: 'COMPOSITE_FORMATION_COUNT_INVALID', message: 'FAN requires an integer count of at least 2' };
      }
      if (!FORMATION_FIELD_RULES.angleDeg.holds(formation.spreadAngleDeg)) {
        return { ok: false, code: 'COMPOSITE_FORMATION_ANGLE_INVALID', message: 'spreadAngleDeg must be finite' };
      }
      // Symmetric about the forward axis: the total spread is the angle
      // between the outermost pair, so a 3×60° fan is -30/0/+30.
      const step = formation.spreadAngleDeg / (formation.count - 1);
      const start = -formation.spreadAngleDeg / 2;
      const transforms = Array.from({ length: formation.count }, (_unused, i) => ({
        x: 0,
        y: 0,
        directionRad: (start + step * i) * DEG_TO_RAD,
      }));
      return { ok: true, transforms };
    }

    case 'PARALLEL': {
      if (!FORMATION_FIELD_RULES.memberCount.holds(formation.count)) {
        return { ok: false, code: 'COMPOSITE_FORMATION_COUNT_INVALID', message: 'PARALLEL requires an integer count of at least 2' };
      }
      if (!FORMATION_FIELD_RULES.spacingM.holds(formation.spacingM)) {
        return { ok: false, code: 'COMPOSITE_FORMATION_SPACING_INVALID', message: 'spacingM must be finite and positive' };
      }
      // Lanes straddle the forward axis evenly, all still facing forward.
      const start = -(formation.spacingM * (formation.count - 1)) / 2;
      const transforms = Array.from({ length: formation.count }, (_unused, i) => ({
        x: start + formation.spacingM * i,
        y: 0,
        directionRad: 0,
      }));
      return { ok: true, transforms };
    }

    case 'RADIAL': {
      if (!FORMATION_FIELD_RULES.memberCount.holds(formation.count)) {
        return { ok: false, code: 'COMPOSITE_FORMATION_COUNT_INVALID', message: 'RADIAL requires an integer count of at least 2' };
      }
      if (!FORMATION_FIELD_RULES.angleDeg.holds(formation.initialAngleDeg)) {
        return { ok: false, code: 'COMPOSITE_FORMATION_ANGLE_INVALID', message: 'initialAngleDeg must be finite' };
      }
      const step = 360 / formation.count;
      const transforms = Array.from({ length: formation.count }, (_unused, i) => ({
        x: 0,
        y: 0,
        directionRad: (formation.initialAngleDeg + step * i) * DEG_TO_RAD,
      }));
      return { ok: true, transforms };
    }

    case 'RING': {
      if (!FORMATION_FIELD_RULES.memberCount.holds(formation.count)) {
        return { ok: false, code: 'COMPOSITE_FORMATION_COUNT_INVALID', message: 'RING requires an integer count of at least 2' };
      }
      if (!FORMATION_FIELD_RULES.angleDeg.holds(formation.initialAngleDeg)) {
        return { ok: false, code: 'COMPOSITE_FORMATION_ANGLE_INVALID', message: 'initialAngleDeg must be finite' };
      }
      if (!FORMATION_FIELD_RULES.radiusM.holds(formation.radiusM)) {
        return { ok: false, code: 'COMPOSITE_FORMATION_RADIUS_INVALID', message: 'radiusM must be finite and positive' };
      }
      const step = 360 / formation.count;
      const transforms = Array.from({ length: formation.count }, (_unused, i) => {
        const angleRad = (formation.initialAngleDeg + step * i) * DEG_TO_RAD;
        return {
          x: formation.radiusM * Math.cos(angleRad),
          y: formation.radiusM * Math.sin(angleRad),
          // A ring member FACES OUTWARD, away from the centre it stands on
          // (ratified 2026-09-19). Position and facing therefore come from
          // one idea — the radius — rather than position coming from the
          // ring while facing was measured off forward, which is what this
          // previously did and what made the formation internally
          // inconsistent: at `initialAngleDeg` 0 a member stood to the side
          // yet aimed straight ahead.
          //
          // The quarter-turn converts "angle around the ring" into this
          // frame's "rotation off forward"; the property it encodes is
          // asserted directly in the tests rather than trusted from here.
          directionRad: angleRad - Math.PI / 2,
        };
      });
      return { ok: true, transforms };
    }

    case 'SCATTER': {
      if (!FORMATION_FIELD_RULES.memberCount.holds(formation.count)) {
        return { ok: false, code: 'COMPOSITE_FORMATION_COUNT_INVALID', message: 'SCATTER requires an integer count of at least 2' };
      }
      if (!FORMATION_FIELD_RULES.radiusM.holds(formation.radiusM)) {
        return { ok: false, code: 'COMPOSITE_FORMATION_RADIUS_INVALID', message: 'radiusM must be finite and positive' };
      }
      const minSpacingM = formation.minSpacingM;
      if (minSpacingM !== undefined && !FORMATION_FIELD_RULES.spacingM.holds(minSpacingM)) {
        return { ok: false, code: 'COMPOSITE_FORMATION_SPACING_INVALID', message: 'minSpacingM, when present, must be finite and positive' };
      }

      const rng = derive(seed, 0x5ca7);
      const placed: ExpandedTransform[] = [];
      for (let i = 0; i < formation.count; i += 1) {
        let accepted = false;
        for (let attempt = 0; attempt < SCATTER_PLACEMENT_ATTEMPTS_PER_MEMBER; attempt += 1) {
          // sqrt keeps the sample uniform over the disc rather than clustered
          // at the centre, so density does not silently depend on radius.
          const r = formation.radiusM * Math.sqrt(rng());
          const theta = rng() * 2 * Math.PI;
          const candidate: ExpandedTransform = { x: r * Math.cos(theta), y: r * Math.sin(theta), directionRad: theta };
          if (
            minSpacingM === undefined ||
            placed.every((p) => Math.hypot(p.x - candidate.x, p.y - candidate.y) >= minSpacingM - 1e-9)
          ) {
            placed.push(candidate);
            accepted = true;
            break;
          }
        }
        if (!accepted) {
          // All-or-nothing: the alternative would be quietly returning fewer
          // meteors than the player asked for, which the contract forbids.
          return {
            ok: false,
            code: 'COMPOSITE_SCATTER_SPACING_INFEASIBLE',
            message: `cannot place ${formation.count} members at least ${String(minSpacingM)}m apart within radius ${formation.radiusM}m`,
          };
        }
      }
      return { ok: true, transforms: placed };
    }

    case 'CHAIN': {
      if (!FORMATION_FIELD_RULES.memberCount.holds(formation.count)) {
        return { ok: false, code: 'COMPOSITE_FORMATION_COUNT_INVALID', message: 'CHAIN requires an integer count of at least 2' };
      }
      // A chain places nothing at the cast: its first member is released as the stage would be, and every later one is
      // placed by the runtime at the enemies the chain strikes. Each member is still its own unit, at the stage frame's origin.
      return { ok: true, transforms: Array.from({ length: formation.count }, () => ({ x: 0, y: 0, directionRad: 0 })) };
    }

    default: {
      const exhaustive: never = formation;
      return { ok: false, code: 'COMPOSITE_FORMATION_KIND_UNKNOWN', message: `unknown formation ${JSON.stringify(exhaustive)}` };
    }
  }
}

type OffsetResult =
  | { readonly ok: true; readonly offsets: readonly number[] }
  | { readonly ok: false; readonly code: string; readonly message: string };

/** Per-member release offsets within one volley, in member order. */
function memberOffsets(schedule: MemberScheduleV1, memberCount: number, seed: number): OffsetResult {
  switch (schedule.kind) {
    case 'SIMULTANEOUS':
      return { ok: true, offsets: new Array<number>(memberCount).fill(0) };

    case 'SEQUENTIAL': {
      if (!isPositiveInt(schedule.intervalTicks)) {
        return { ok: false, code: 'COMPOSITE_MEMBER_INTERVAL_INVALID', message: 'SEQUENTIAL requires an integer intervalTicks of at least 1' };
      }
      return { ok: true, offsets: Array.from({ length: memberCount }, (_unused, i) => schedule.intervalTicks * i) };
    }

    case 'STAGGERED': {
      if (!isPositiveInt(schedule.windowTicks)) {
        return { ok: false, code: 'COMPOSITE_MEMBER_WINDOW_INVALID', message: 'STAGGERED requires an integer windowTicks of at least 1' };
      }
      if (schedule.seedPolicy !== 'CAST_SEED') {
        return { ok: false, code: 'COMPOSITE_MEMBER_SEED_POLICY_UNKNOWN', message: 'STAGGERED requires seedPolicy CAST_SEED' };
      }
      const rng = derive(seed, 0x57a6);
      // Integer ticks only: the scheduler owns integral time, so a fractional
      // offset here would be rounded somewhere less visible.
      const offsets = Array.from({ length: memberCount }, () => Math.floor(rng() * (schedule.windowTicks + 1)));
      return { ok: true, offsets };
    }

    default: {
      const exhaustive: never = schedule;
      return { ok: false, code: 'COMPOSITE_MEMBER_SCHEDULE_KIND_UNKNOWN', message: `unknown member schedule ${JSON.stringify(exhaustive)}` };
    }
  }
}

type VolleyResult =
  | { readonly ok: true; readonly offsets: readonly number[] }
  | { readonly ok: false; readonly code: string; readonly message: string };

function volleyOffsets(volleys: VolleyScheduleV1): VolleyResult {
  if (volleys.kind === 'ONCE') {
    if (volleys.count !== 1) {
      return { ok: false, code: 'COMPOSITE_VOLLEY_COUNT_INVALID', message: 'ONCE must declare count 1' };
    }
    return { ok: true, offsets: [0] };
  }
  if (!isPositiveInt(volleys.count) || volleys.count < 2) {
    return { ok: false, code: 'COMPOSITE_VOLLEY_COUNT_INVALID', message: 'REPEAT requires an integer count of at least 2' };
  }
  if (!isPositiveInt(volleys.intervalTicks)) {
    // A zero interval is simultaneity dressed as repetition: three "separate"
    // thrusts landing on one tick. Whether that is ever legal is a runtime and
    // pricing question, so it is refused rather than assumed.
    return { ok: false, code: 'COMPOSITE_VOLLEY_INTERVAL_INVALID', message: 'REPEAT requires an integer intervalTicks of at least 1' };
  }
  return { ok: true, offsets: Array.from({ length: volleys.count }, (_unused, i) => volleys.intervalTicks * i) };
}

function checkGroupingCompatibility(
  hitGrouping: HitGroupingV1,
  memberSchedule: MemberScheduleV1,
): SpatialExpansionResult | undefined {
  if (hitGrouping === 'SHARED_UNION' && memberSchedule.kind !== 'SIMULTANEOUS') {
    return fail(
      'COMPOSITE_SHARED_UNION_REQUIRES_SIMULTANEOUS',
      'SHARED_UNION members must be SIMULTANEOUS: a single attack unit resolves at a single moment',
    );
  }
  return undefined;
}

/**
 * Expand a pattern into its attack units.
 *
 * `castSeed` must be a cast-local deterministic seed produced and snapshotted
 * by the execution layer that owns the cast's RNG. It is never `Date.now()`
 * and never a fresh random draw at this boundary; this function only consumes
 * what it is handed.
 *
 * On any rejection nothing is emitted: a pattern expands completely or not at
 * all, so no caller can observe a partially-placed formation.
 */
export function expandSpatialPattern(
  pattern: SpatialAttackPatternV1,
  castSeed: number,
): SpatialExpansionResult {
  if (!Number.isFinite(castSeed)) {
    return fail('COMPOSITE_CAST_SEED_INVALID', 'castSeed must be a finite number supplied by the cast RNG owner');
  }

  const incompatible = checkGroupingCompatibility(pattern.hitGrouping, pattern.memberSchedule);
  if (incompatible !== undefined) return incompatible;

  const expandedFormation = expandFormation(pattern.formation, castSeed);
  if (!expandedFormation.ok) return fail(expandedFormation.code, expandedFormation.message);
  const formation = formationIsWorldFixed(pattern.formation.kind)
    ? { ...expandedFormation, transforms: expandedFormation.transforms.map((transform): ExpandedTransform => ({ ...transform, worldFixed: true })) }
    : expandedFormation;

  const volleys = volleyOffsets(pattern.volleys);
  if (!volleys.ok) return fail(volleys.code, volleys.message);

  const members = memberOffsets(pattern.memberSchedule, formation.transforms.length, castSeed);
  if (!members.ok) return fail(members.code, members.message);

  const units: ExpandedAttackUnit[] = [];
  for (const [volleyIndex, volleyOffset] of volleys.offsets.entries()) {
    if (pattern.hitGrouping === 'SHARED_UNION') {
      // One attack, every footprint. The member count changes the space this
      // attack searches, never how many times a target can be hit by it.
      units.push({
        volleyIndex,
        memberIndex: null,
        memberIndices: formation.transforms.map((_unused, i) => i),
        releaseOffsetTicks: volleyOffset,
        transforms: formation.transforms,
      });
      continue;
    }
    formation.transforms.forEach((transform, memberIndex) => {
      units.push({
        volleyIndex,
        memberIndex,
        memberIndices: [memberIndex],
        releaseOffsetTicks: volleyOffset + members.offsets[memberIndex]!,
        transforms: [transform],
      });
    });
  }

  // Stable total order, so replay, telemetry and the scheduler all agree on
  // "which attack was that" without consulting insertion order.
  units.sort(
    (a, b) =>
      a.volleyIndex - b.volleyIndex ||
      a.releaseOffsetTicks - b.releaseOffsetTicks ||
      (a.memberIndex ?? -1) - (b.memberIndex ?? -1),
  );

  return { ok: true, units };
}

/**
 * How many independent attack units one cast of this pattern produces, and
 * how many footprints they cover between them.
 *
 * Counted from the same expansion the runtime uses rather than re-derived
 * from the pattern, so the technical gate and the scheduler can never
 * disagree about how many attacks a skill really makes.
 */
export function countAttackUnits(pattern: SpatialAttackPatternV1, castSeed: number): number | undefined {
  const expanded = expandSpatialPattern(pattern, castSeed);
  return expanded.ok ? expanded.units.length : undefined;
}
